import { isJsonObject, isJsonString, type JsonObject, type JsonValue } from '@/api/json';
import type { HttpErrorContext } from '@/lib/http';

/**
 * Narrow a caught value to an Error.
 *
 * JavaScript lets anything be thrown, so `catch` hands back a value with no
 * contract at all — the only place in this app where that is true. Every error
 * handler downstream reads an Error, so the narrowing happens once, here, and
 * the original value is kept as the new error's `cause` rather than discarded.
 */
export function toError(cause: unknown): Error {
    return cause instanceof Error ? cause : new Error(String(cause), { cause });
}

export interface LaravelErrorJson {
    message: string;
    errors?: JsonObject;
}

export interface LaravelErrorCause {
    code?: string;
    message?: string;
    url?: string;
    response?: {
        status: number;
        headers?: Headers | Record<string, string>;
        /** What the failing response carried: parsed JSON, or a Blob for a download. */
        data?: JsonValue | Blob;
    };
}

export default class LaravelError extends Error {
    constructor(message: string, cause: LaravelErrorCause | null = null) {
        super(message);
        this.localCause = cause;
    }

    localCause: LaravelErrorCause | null = null;
    errors: Record<string, string | string[]> = {};

    static fromHttpResponse(context: HttpErrorContext): LaravelError {
        const body = context.body;
        const parsed = laravelErrorJson(body);
        const error = parsed
            ? this.fromJSON(parsed)
            : new LaravelError(context.response.statusText || `Request failed with status code ${context.status}`);

        error.localCause = {
            url: context.url,
            response: {
                status: context.status,
                headers: context.response.headers,
                data: body,
            },
        };

        return error;
    }

    static fromTransportError(error: Error): LaravelError {
        if (isAbortError(error)) {
            return new LaravelError(error.message || 'The request was cancelled.', {
                code: 'ERR_CANCELED',
                message: error.message,
            });
        }

        return new LaravelError(error.message || 'Network Error', {
            code: 'ERR_NETWORK',
            message: error.message,
        });
    }

    static fromJSON(json: LaravelErrorJson): LaravelError {
        const error = new LaravelError(json.message);
        error.errors = normalizeErrors(json.errors);
        error.localCause = null;
        return error;
    }
}

/**
 * Whether the request was cancelled rather than failed. DOMException is absent
 * in some non-browser environments, where nothing can have thrown one.
 */
function isAbortError(error: Error): error is DOMException {
    return typeof DOMException !== 'undefined' && error instanceof DOMException && error.name === 'AbortError';
}

/** Laravel's error envelope, when that is what the failing response carried. */
function laravelErrorJson(body: JsonValue): LaravelErrorJson | null {
    if (!isJsonObject(body)) {
        return null;
    }

    const message = body['message'];
    if (!isJsonString(message)) {
        return null;
    }

    const parsed: LaravelErrorJson = { message };
    const errors = body['errors'];
    if (isJsonObject(errors)) {
        parsed.errors = errors;
    }

    return parsed;
}

function normalizeErrors(errors: JsonObject | undefined): Record<string, string | string[]> {
    if (!errors) {
        return {};
    }

    return Object.fromEntries(
        Object.entries(errors).map(([key, value]) => {
            if (Array.isArray(value)) {
                return [key, value.map(String)];
            }

            return [key, String(value)];
        }),
    );
}
